import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as applicationService from '../../services/partnerApplication.service';

/**
 * Same quiet line as "You've been mentioned", at the bottom of "What's on
 * tap for today" (Jenn, 2026-10-07): an affiliate (or, later, a partner)
 * applied, or sent in their landing page info. Only for people who can
 * manage affiliates. In-app only, no email.
 */
export default function AffiliateWaitingBar() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { applications, landingPages } = await applicationService.getWaiting();
      setItems([
        ...applications.map(a => ({ key: `a-${a.id}`, name: a.name, text: `applied to be ${a.program === 'partner' ? 'a partner' : 'an affiliate'}`, to: '/admin/affiliate-manager#todo' })),
        // Jenn, 2026-10-07: open the page she works on — that person's own page,
        // where their landing page info is reviewed and approved.
        ...landingPages.map(p => ({ key: `l-${p.id}`, name: p.name, text: 'sent in their landing page info', to: p.partner_type === 'partner' ? `/admin/partners/${p.id}` : `/admin/affiliates/${p.id}` })),
      ]);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', load);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  if (items.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-2 text-sm font-medium text-reset-terracotta hover:underline"
      >
        <span className="w-2 h-2 rounded-full bg-reset-terracotta" />
        Affiliates &amp; partners waiting on you ({items.length})
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5">
          {items.map(i => (
            <button
              key={i.key}
              onClick={() => navigate(i.to)}
              className="w-full text-left bg-reset-cream rounded-lg px-3 py-2 text-xs hover:bg-reset-creamDark transition-colors"
            >
              <span className="font-medium text-reset-brown">{i.name}</span>
              <span className="text-gray-500"> {i.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
